import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/bi')({ component: AdminBI });

const money = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const compact = (v: number) =>
  v >= 1000 ? `R$${(v / 1000).toFixed(1)}k` : v > 0 ? `R$${v.toFixed(0)}` : '—';

type BIData = {
  months: { label: string; gmv: number; platformRev: number; sessions: number; signups: number }[];
  totals: {
    gmv6m: number; platformRev6m: number; signups6m: number;
    lifetimeGmv: number; lifetimePlatformRev: number; lifetimePayouts: number; totalSessions: number;
  };
  planMix: Record<string, number>;
};

function AdminBI() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-bi'],
    queryFn: () => api.get('admin/bi').json<BIData>(),
  });

  if (isLoading || !data) {
    return (
      <>
        <AdminPageHeader title="Business Intelligence" subtitle="Métricas dos últimos 6 meses" />
        <div className="bg-white rounded-3xl p-12 animate-pulse h-64 border border-slate-100" />
      </>
    );
  }

  const maxGmv = Math.max(1, ...data.months.map((m) => m.gmv));
  const maxAct = Math.max(1, ...data.months.map((m) => Math.max(m.sessions, m.signups)));
  const planTotal = Object.values(data.planMix).reduce((s, n) => s + n, 0);

  return (
    <>
      <AdminPageHeader title="Business Intelligence" subtitle="Métricas dos últimos 6 meses" />

      {/* Top KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Kpi label="GMV (6m)" value={money(data.totals.gmv6m)} sub="volume bruto" tone="teal" />
        <Kpi label="Receita Nosso Ello (6m)" value={money(data.totals.platformRev6m)} sub="taxa cobrada" tone="coral" />
        <Kpi label="Cadastros (6m)" value={String(data.totals.signups6m)} sub="novos usuários" />
        <Kpi label="Lifetime payouts" value={money(data.totals.lifetimePayouts)} sub={`${data.totals.totalSessions} sessões`} />
        <Kpi label="Lifetime fee" value={money(data.totals.lifetimePlatformRev)} sub="receita acumulada" tone="coral" />
      </section>

      {/* GMV chart */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm mb-5">
        <h3 className="font-bold text-slate-800 mb-1">GMV mensal</h3>
        <p className="text-xs text-slate-500 mb-4">Soma das sessões DONE por mês</p>
        <div className="flex items-end gap-3 h-44">
          {data.months.map((m) => (
            <div key={m.label} className="flex-1 flex flex-col items-center gap-2">
              <span className="text-[10px] tabular-nums text-slate-500 font-semibold whitespace-nowrap">
                {compact(m.gmv)}
              </span>
              <div className="w-full bg-slate-50 rounded-t-md relative" style={{ height: `${(m.gmv / maxGmv) * 130}px`, minHeight: '4px' }}>
                <div className="absolute inset-0 bg-gradient-to-t from-teal-500 to-teal-300 rounded-t-md" />
              </div>
              <span className="text-[10px] uppercase text-slate-400 font-semibold">{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Atividade — sessões vs cadastros */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-1">Atividade</h3>
          <p className="text-xs text-slate-500 mb-4">Sessões realizadas vs novos cadastros</p>
          <div className="flex items-end gap-2 h-44">
            {data.months.map((m) => (
              <div key={m.label} className="flex-1 flex flex-col items-center gap-2">
                <div className="flex items-end gap-0.5 w-full h-32">
                  <div className="flex-1 bg-teal-500 rounded-t" style={{ height: `${(m.sessions / maxAct) * 100}%`, minHeight: m.sessions > 0 ? '4px' : '0' }} title={`${m.sessions} sessões`} />
                  <div className="flex-1 bg-coral-300 rounded-t" style={{ height: `${(m.signups / maxAct) * 100}%`, minHeight: m.signups > 0 ? '4px' : '0' }} title={`${m.signups} cadastros`} />
                </div>
                <span className="text-[10px] uppercase text-slate-400 font-semibold">{m.label}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-sm bg-teal-500" /> Sessões</span>
            <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-sm bg-coral-300" /> Cadastros</span>
          </div>
        </div>

        {/* Mix de planos */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-3">Mix de planos</h3>
          <div className="space-y-3">
            {(['FREE', 'PRO', 'PREMIUM'] as const).map((tier) => {
              const count = data.planMix[tier] ?? 0;
              const pct = planTotal > 0 ? (count / planTotal) * 100 : 0;
              const color = tier === 'PREMIUM' ? 'bg-amber-500' : tier === 'PRO' ? 'bg-teal-500' : 'bg-slate-300';
              return (
                <div key={tier}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-slate-700">{tier}</span>
                    <span className="text-slate-500">{count} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={cn('h-full transition-all', color)} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

function Kpi({
  label, value, sub, tone = 'slate',
}: { label: string; value: string; sub?: string; tone?: 'teal' | 'coral' | 'slate' }) {
  const styles = {
    teal: 'border-teal-100 bg-teal-50/40',
    coral: 'border-coral-100 bg-coral-50/40',
    slate: 'border-slate-200 bg-white',
  };
  return (
    <div className={cn('rounded-xl border p-4', styles[tone])}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-extrabold mt-1 text-slate-900">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
